import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Dialog,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Chip,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  InputAdornment,
  Collapse,
  Checkbox
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchGSTInvoiceBreakdown } from '../../store/gstSettlementStore/gstSettlementThunk';
import { clearInvoiceBreakdown } from '../../store/gstSettlementStore/gstSettlementSlice';
import { ApiStatus } from '../../utils/api.types';
import { IGSTPaymentForm } from '../../utils/gstSettlement.types';

interface GSTPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  paymentForm: IGSTPaymentForm;
  onPaymentFormChange: (field: string, value: any) => void;
  onReturnDetailsChange: (field: string, value: any) => void;
  onSubmit: (selectedPartyIds?: string[]) => void;
  paymentStatus: ApiStatus;
  bankLedgers: any[];
}

const GSTPaymentDialog: React.FC<GSTPaymentDialogProps> = ({
  open,
  onClose,
  paymentForm,
  onPaymentFormChange,
  onReturnDetailsChange,
  onSubmit,
  paymentStatus,
  bankLedgers
}) => {
  const dispatch = useAppDispatch();
  const { invoiceBreakdown, expandedPartyInvoices, invoiceBreakdownStatus } = useAppSelector(
    (state) => state.gstSettlement
  );

  const [breakdownExpanded, setBreakdownExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedParties, setExpandedParties] = useState<Set<string>>(new Set());
  const [selectedParties, setSelectedParties] = useState<Set<string>>(new Set());
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  // Recalculate GST amounts based on selected parties
  const updateGSTAmountsFromSelection = useCallback((selected: Set<string>) => {
    if (!invoiceBreakdown?.partySummaries) return;
    let cgst = 0, sgst = 0, igst = 0;
    invoiceBreakdown.partySummaries.forEach(party => {
      if (selected.has(party.partyId)) {
        cgst += party.totalCGST;
        sgst += party.totalSGST;
        igst += party.totalIGST;
      }
    });
    onPaymentFormChange('cgstPaid', Number(cgst.toFixed(2)));
    onPaymentFormChange('sgstPaid', Number(sgst.toFixed(2)));
    onPaymentFormChange('igstPaid', Number(igst.toFixed(2)));
  }, [invoiceBreakdown, onPaymentFormChange]);

  // Auto-select all parties when breakdown data loads
  useEffect(() => {
    if (invoiceBreakdown?.partySummaries && invoiceBreakdown.partySummaries.length > 0) {
      const allIds = new Set(invoiceBreakdown.partySummaries.map(p => p.partyId));
      setSelectedParties(allIds);
      updateGSTAmountsFromSelection(allIds);
    }
  }, [invoiceBreakdown?.partySummaries]);

  // Toggle party selection
  const handlePartySelect = useCallback((partyId: string) => {
    const newSelected = new Set(selectedParties);
    if (newSelected.has(partyId)) {
      newSelected.delete(partyId);
    } else {
      newSelected.add(partyId);
    }
    setSelectedParties(newSelected);
    updateGSTAmountsFromSelection(newSelected);
  }, [selectedParties, updateGSTAmountsFromSelection]);

  // Select/Deselect all
  const handleSelectAll = useCallback(() => {
    if (!invoiceBreakdown?.partySummaries) return;
    const allSelected = invoiceBreakdown.partySummaries.every(p => selectedParties.has(p.partyId));
    let newSelected: Set<string>;
    if (allSelected) {
      newSelected = new Set();
    } else {
      newSelected = new Set(invoiceBreakdown.partySummaries.map(p => p.partyId));
    }
    setSelectedParties(newSelected);
    updateGSTAmountsFromSelection(newSelected);
  }, [invoiceBreakdown, selectedParties, updateGSTAmountsFromSelection]);

  // Fetch breakdown when accordion is expanded
  const handleBreakdownToggle = useCallback((_: any, isExpanded: boolean) => {
    setBreakdownExpanded(isExpanded);
    if (isExpanded && paymentForm.paymentPeriod.fromDate && paymentForm.paymentPeriod.toDate) {
      dispatch(fetchGSTInvoiceBreakdown({
        fromDate: paymentForm.paymentPeriod.fromDate,
        toDate: paymentForm.paymentPeriod.toDate
      }));
    }
  }, [dispatch, paymentForm.paymentPeriod]);

  // Debounced search
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      if (paymentForm.paymentPeriod.fromDate && paymentForm.paymentPeriod.toDate) {
        dispatch(fetchGSTInvoiceBreakdown({
          fromDate: paymentForm.paymentPeriod.fromDate,
          toDate: paymentForm.paymentPeriod.toDate,
          search: value || undefined
        }));
      }
    }, 300);
    setSearchTimeout(timeout);
  }, [dispatch, paymentForm.paymentPeriod, searchTimeout]);

  // Toggle party expansion and fetch invoices
  const toggleParty = useCallback((partyId: string) => {
    const newExpanded = new Set(expandedParties);
    if (newExpanded.has(partyId)) {
      newExpanded.delete(partyId);
    } else {
      newExpanded.add(partyId);
      // Fetch invoices for this party if not already loaded
      if (!expandedPartyInvoices[partyId]) {
        dispatch(fetchGSTInvoiceBreakdown({
          fromDate: paymentForm.paymentPeriod.fromDate,
          toDate: paymentForm.paymentPeriod.toDate,
          partyId
        }));
      }
    }
    setExpandedParties(newExpanded);
  }, [dispatch, expandedParties, expandedPartyInvoices, paymentForm.paymentPeriod]);

  // Clean up on close
  useEffect(() => {
    if (!open) {
      setBreakdownExpanded(false);
      setSearchTerm('');
      setExpandedParties(new Set());
      setSelectedParties(new Set());
      dispatch(clearInvoiceBreakdown());
    }
  }, [open, dispatch]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Get available return types based on taxpayer type
  const getAvailableReturnTypes = () => {
    const taxpayerType = paymentForm.returnDetails.taxpayerType;

    if (taxpayerType === 'REGULAR') {
      return [
        { value: 'GSTR-1', label: 'GSTR-1 (Sales)' },
        { value: 'GSTR-3B', label: 'GSTR-3B (Monthly Return)' },
        { value: 'GSTR-9', label: 'GSTR-9 (Annual Return)' },
        { value: 'GSTR-9C', label: 'GSTR-9C (Reconciliation)' }
      ];
    } else if (taxpayerType === 'QRMP') {
      return [
        { value: 'PMT-06', label: 'PMT-06 (Monthly Payment)' },
        { value: 'GSTR-1', label: 'GSTR-1 (Quarterly Sales)' },
        { value: 'GSTR-3B', label: 'GSTR-3B (Quarterly Return)' }
      ];
    } else if (taxpayerType === 'COMPOSITION') {
      return [
        { value: 'CMP-08', label: 'CMP-08 (Quarterly Return)' },
        { value: 'GSTR-4', label: 'GSTR-4 (Annual Return)' }
      ];
    }
    return [];
  };

  // Get available payment frequencies based on taxpayer type and return type
  const getAvailableFrequencies = () => {
    const taxpayerType = paymentForm.returnDetails.taxpayerType;
    const returnType = paymentForm.returnDetails.returnType;

    if (taxpayerType === 'REGULAR') {
      if (returnType === 'GSTR-9' || returnType === 'GSTR-9C') {
        return [{ value: 'YEARLY', label: 'Yearly' }];
      }
      return [{ value: 'MONTHLY', label: 'Monthly' }];
    } else if (taxpayerType === 'QRMP') {
      if (returnType === 'PMT-06') {
        return [{ value: 'MONTHLY', label: 'Monthly' }];
      }
      return [{ value: 'QUARTERLY', label: 'Quarterly' }];
    } else if (taxpayerType === 'COMPOSITION') {
      if (returnType === 'GSTR-4') {
        return [{ value: 'YEARLY', label: 'Yearly' }];
      }
      return [{ value: 'QUARTERLY', label: 'Quarterly' }];
    }
    return [{ value: 'MONTHLY', label: 'Monthly' }];
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <Box>
        {/* Header with buttons */}
        <Grid container>
          <Grid item xs={12} p={2} pl={2} bgcolor='#e9ecf1' style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, height: 50 }}>
            <div style={{ float: 'right', marginTop: -7, marginRight: -5 }}>
              <Button
                variant="contained"
                onClick={() => onSubmit(selectedParties.size > 0 ? Array.from(selectedParties) : undefined)}
                disabled={
                  paymentStatus === ApiStatus.LOADING ||
                  !paymentForm.challanNumber ||
                  !paymentForm.bankAccount ||
                  (paymentForm.cgstPaid === 0 && paymentForm.sgstPaid === 0 && paymentForm.igstPaid === 0)
                }
                style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                size='small'
              >
                {paymentStatus === ApiStatus.LOADING ? <CircularProgress size={20} /> : 'Record Payment'}
              </Button>
              <Button
                style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                variant="contained"
                size='small'
                onClick={onClose}
              >
                Cancel
              </Button>
            </div>
            <h3 style={{ marginTop: -2 }}>Record GST Payment to Government</h3>
          </Grid>
        </Grid>

        {/* Form Content */}
        <Grid container p={2} spacing={2}>
          {/* Return Details */}
          <Grid item xs={12} mt={1}>
            <Divider style={{ marginRight: -8 }}>
              <Chip label="GST Return Details" size="small" />
            </Divider>
          </Grid>

          <Grid item xs={4} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Taxpayer Type*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.returnDetails.taxpayerType}
              onChange={(e: SelectChangeEvent<any>) => onReturnDetailsChange('taxpayerType', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              <MenuItem value="REGULAR">Regular</MenuItem>
              <MenuItem value="QRMP">QRMP (Quarterly Return Monthly Payment)</MenuItem>
              <MenuItem value="COMPOSITION">Composition</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={4}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Return Type*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.returnDetails.returnType}
              onChange={(e: SelectChangeEvent<any>) => onReturnDetailsChange('returnType', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              {getAvailableReturnTypes().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={4} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Payment Frequency*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.returnDetails.paymentFrequency}
              onChange={(e: SelectChangeEvent<any>) => onReturnDetailsChange('paymentFrequency', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              {getAvailableFrequencies().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          {/* GST Amounts */}
          <Grid item xs={12} mt={1}>
            <Divider style={{ marginRight: -8 }}>
              <Chip label="GST Amounts" size="small" />
            </Divider>
          </Grid>

          <Grid item xs={4} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>CGST Paid</h5>
            <TextField
              placeholder='CGST Paid'
              className='c-text-field-4'
              type="number"
              value={paymentForm.cgstPaid}
              onChange={(e) => onPaymentFormChange('cgstPaid', parseFloat(e.target.value) || 0)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={4}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>SGST Paid</h5>
            <TextField
              placeholder='SGST Paid'
              className='c-text-field-4'
              type="number"
              value={paymentForm.sgstPaid}
              onChange={(e) => onPaymentFormChange('sgstPaid', parseFloat(e.target.value) || 0)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={4} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>IGST Paid</h5>
            <TextField
              placeholder='IGST Paid'
              className='c-text-field-4'
              type="number"
              value={paymentForm.igstPaid}
              onChange={(e) => onPaymentFormChange('igstPaid', parseFloat(e.target.value) || 0)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1, color: '#1976d2', fontWeight: 600 }}>
              Total Amount: {formatCurrency(paymentForm.cgstPaid + paymentForm.sgstPaid + paymentForm.igstPaid)}
            </Typography>
          </Grid>

          {/* Invoice Breakdown Section */}
          <Grid item xs={12} mt={1}>
            <Accordion
              expanded={breakdownExpanded}
              onChange={handleBreakdownToggle}
              sx={{ border: '1px solid #e0e0e0', boxShadow: 'none', borderRadius: '8px !important', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Invoice Breakdown</Typography>
                  {invoiceBreakdown && (
                    <Chip
                      label={`${invoiceBreakdown.totalParties} parties`}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1.5, pt: 0 }}>
                {/* Search */}
                <TextField
                  placeholder="Search by party name..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{ mb: 1.5, mt: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: '#9da7b7' }} />
                      </InputAdornment>
                    )
                  }}
                />

                {/* Loading state */}
                {invoiceBreakdownStatus === ApiStatus.LOADING && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {/* Party list */}
                {invoiceBreakdown?.partySummaries && invoiceBreakdownStatus !== ApiStatus.LOADING && (
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, width: 36 }} padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={invoiceBreakdown.partySummaries.length > 0 && invoiceBreakdown.partySummaries.every(p => selectedParties.has(p.partyId))}
                              indeterminate={selectedParties.size > 0 && !invoiceBreakdown.partySummaries.every(p => selectedParties.has(p.partyId))}
                              onChange={handleSelectAll}
                              sx={{ p: 0 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75 }}>Party Name</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75 }}>Invoices</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75 }}>CGST</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75 }}>SGST</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75 }}>IGST</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75 }}>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoiceBreakdown.partySummaries.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 3, color: '#9da7b7', fontSize: '0.8rem' }}>
                              No unsettled invoices found
                            </TableCell>
                          </TableRow>
                        ) : (
                          invoiceBreakdown.partySummaries.map((party) => (
                            <React.Fragment key={party.partyId}>
                              {/* Party summary row */}
                              <TableRow
                                hover
                                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f9f9f9' }, opacity: selectedParties.has(party.partyId) ? 1 : 0.5 }}
                              >
                                <TableCell padding="checkbox" sx={{ py: 0.75 }}>
                                  <Checkbox
                                    size="small"
                                    checked={selectedParties.has(party.partyId)}
                                    onChange={(e) => { e.stopPropagation(); handlePartySelect(party.partyId); }}
                                    sx={{ p: 0 }}
                                  />
                                </TableCell>
                                <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }} onClick={() => toggleParty(party.partyId)}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <IconButton size="small" sx={{ p: 0, width: 18, height: 18 }}>
                                      {expandedParties.has(party.partyId) ? (
                                        <ExpandLessIcon sx={{ fontSize: 16 }} />
                                      ) : (
                                        <ExpandMoreIcon sx={{ fontSize: 16 }} />
                                      )}
                                    </IconButton>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{party.partyName}</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.75, fontSize: '0.8rem' }}>
                                  <Chip label={party.invoiceCount} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                                </TableCell>
                                <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem' }}>{formatCurrency(party.totalCGST)}</TableCell>
                                <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem' }}>{formatCurrency(party.totalSGST)}</TableCell>
                                <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem' }}>{formatCurrency(party.totalIGST)}</TableCell>
                                <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 600 }}>{formatCurrency(party.totalGST)}</TableCell>
                              </TableRow>

                              {/* Expanded invoice rows */}
                              {expandedParties.has(party.partyId) && (
                                <TableRow>
                                  <TableCell colSpan={7} sx={{ p: 0, border: 'none' }}>
                                    <Collapse in={expandedParties.has(party.partyId)}>
                                      <Box sx={{ backgroundColor: '#fafafa', px: 2, py: 1 }}>
                                        {!expandedPartyInvoices[party.partyId] ? (
                                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                                            <CircularProgress size={18} />
                                          </Box>
                                        ) : (
                                          <Table size="small">
                                            <TableBody>
                                              {expandedPartyInvoices[party.partyId].map((inv, idx) => (
                                                <TableRow key={inv.journalEntryId || inv.invoiceId || idx}>
                                                  <TableCell sx={{ py: 0.5, fontSize: '0.75rem', pl: 4, border: 'none' }}>
                                                    {inv.invoiceNumber}
                                                  </TableCell>
                                                  <TableCell sx={{ py: 0.5, fontSize: '0.75rem', border: 'none', color: '#666' }}>
                                                    {new Date(inv.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                  </TableCell>
                                                  <TableCell sx={{ py: 0.5, fontSize: '0.7rem', border: 'none' }}>
                                                    <Chip label={inv.voucherType} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                                                  </TableCell>
                                                  <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', border: 'none' }}>{formatCurrency(inv.cgst)}</TableCell>
                                                  <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', border: 'none' }}>{formatCurrency(inv.sgst)}</TableCell>
                                                  <TableCell align="right" sx={{ py: 0.5, fontSize: '0.75rem', border: 'none' }}>{formatCurrency(inv.igst)}</TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        )}
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          ))
                        )}
                        {/* Selected totals footer */}
                        {invoiceBreakdown.partySummaries.length > 0 && (
                          <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                            <TableCell padding="checkbox" sx={{ py: 0.75 }} />
                            <TableCell sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 700 }}>
                              Selected ({selectedParties.size} of {invoiceBreakdown.partySummaries.length})
                            </TableCell>
                            <TableCell align="center" sx={{ py: 0.75 }} />
                            <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 700, color: '#1976d2' }}>
                              {formatCurrency(paymentForm.cgstPaid)}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 700, color: '#1976d2' }}>
                              {formatCurrency(paymentForm.sgstPaid)}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 700, color: '#1976d2' }}>
                              {formatCurrency(paymentForm.igstPaid)}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 700, color: '#1976d2' }}>
                              {formatCurrency(paymentForm.cgstPaid + paymentForm.sgstPaid + paymentForm.igstPaid)}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Payment Details */}
          <Grid item xs={12} mt={1}>
            <Divider style={{ marginRight: -8 }}>
              <Chip label="Payment Details" size="small" />
            </Divider>
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Payment Date*</h5>
            <TextField
              placeholder='Payment Date'
              className='c-text-field-4'
              type="date"
              value={paymentForm.paymentDate}
              onChange={(e) => onPaymentFormChange('paymentDate', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              required
              size='small'
              InputProps={{
                disableUnderline: true
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Challan Number*</h5>
            <TextField
              placeholder='Challan Number'
              className='c-text-field-4'
              value={paymentForm.challanNumber}
              onChange={(e) => onPaymentFormChange('challanNumber', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              required
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Payment Mode*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.paymentMode}
              onChange={(e: SelectChangeEvent<any>) => onPaymentFormChange('paymentMode', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              <MenuItem value="NEFT">NEFT</MenuItem>
              <MenuItem value="RTGS">RTGS</MenuItem>
              <MenuItem value="UPI">UPI</MenuItem>
              <MenuItem value="NETBANKING">Net Banking</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Bank Account*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.bankAccount}
              onChange={(e: SelectChangeEvent<any>) => onPaymentFormChange('bankAccount', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px',
                    maxHeight: 300
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              {bankLedgers.length === 0 ? (
                <MenuItem disabled value="">
                  No bank accounts found
                </MenuItem>
              ) : (
                bankLedgers.map((ledger: any) => (
                  <MenuItem key={ledger._id} value={ledger._id}>
                    {ledger.ledgerName}
                  </MenuItem>
                ))
              )}
            </Select>
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Reference Number (Optional)</h5>
            <TextField
              placeholder='Reference Number'
              className='c-text-field-4'
              value={paymentForm.referenceNumber}
              onChange={(e) => onPaymentFormChange('referenceNumber', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Remarks (Optional)</h5>
            <TextField
              placeholder='Remarks'
              className='c-text-field-4'
              multiline
              rows={3}
              value={paymentForm.remarks}
              onChange={(e) => onPaymentFormChange('remarks', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default GSTPaymentDialog;
